// External
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { of, Observable, BehaviorSubject } from 'rxjs';
import { catchError, map, finalize, skipWhile } from 'rxjs/operators';
import { tv4 } from 'tv4';

// Internal
import { UtilsCommonService } from '../../../../common/utils/utils-common.service';
import { ConnectUserInfoService } from '../../requests/connect-user-info-service/connect-user-info-service';
import { ConnectMgmtService } from '../../requests/connect-mgmt-service/connect-mgmt.service';
import { ValuesService } from '../../../../common/values/values.service';
import { UsefulService } from '../../global/useful/useful.service';
import { MessageService } from '../../core/message.service';

@Injectable({
    providedIn: 'root'
})
export class AccountService {

    account: any = {};
    lock = false;

    private markToUpdatePassword = true;
    private marktoUpdateSessions = true;
    private readonly onlistSessions: BehaviorSubject<string> = new BehaviorSubject<string>(this.valuesService.processServiceState.WAITING);
    private readonly onListPasswordStatus: BehaviorSubject<string> = new BehaviorSubject<string>(this.valuesService.processServiceState.WAITING);

    constructor(
        readonly connectMgmtService     : ConnectMgmtService,
        readonly connectUserInfoService : ConnectUserInfoService,
        readonly router                 : Router,
        readonly usefulService          : UsefulService,
        readonly utilsService           : UtilsCommonService,
        readonly valuesService          : ValuesService,
        private readonly messageService : MessageService
    ) {
        /* Used for updating the request in order to write them into th db by the service worker */
        this.messageService.getDynamicSubject(this.valuesService.events.resetRequestsForServiceWorker)
        .subscribe({
            next: () => {
                this.updateSesions();
                this.updatePassword();
            }
        });
    }

    list(): Observable<any> {
        if (!this.utilsService.isEmptyObject(this.account)) {
            return of(this.account);
        }
        return this.connectUserInfoService.getInfoUser()
        .pipe(
            map(
                resp => {
                    this.lock = true;
                    this.account = resp.result;
                    return of(this.account);
                }
            ),
            catchError((err)=>{
                throw err;
            }),
            finalize(
                () => {
                    this.lock = false;
                }
            )
        )
    }

    get() {
        return this.account;
    }

    set(data) {
        if (this.utilsService.isObject(data)) {
            this.account = JSON.parse(JSON.stringify(data));
        } else {
            console.error('Invalid params', tv4.error);
        }
    }

    listPasswordStatus(): Observable<any> {
        if (!this.markToUpdatePassword){
            return of(this.account);
        }

        if (this.onListPasswordStatus.value === this.valuesService.processServiceState.INPROGRESS) {
            return this.onListPasswordStatus.asObservable()
                .pipe(
                    skipWhile(res => res !== this.valuesService.processServiceState.DONE)
                );
        } else {
            this.onListPasswordStatus.next(this.valuesService.processServiceState.INPROGRESS);
            return this.connectMgmtService.getPasswordStatus()
            .pipe(
                map(
                    resp => {
                        if (!this.account['password']) {
                            this.account['password'] = {};
                        }
                        this.account['password']['passwordSet'] = resp; // true || false
                        this.markToUpdatePassword = false;
                        this.onListPasswordStatus.next(this.valuesService.processServiceState.DONE);
                    }
                ),
                catchError( (err)=> {
                    if (!this.account['password']) {
                        this.account['password'] = {};
                    }

                    this.markToUpdatePassword = true;
                    this.onListPasswordStatus.next(this.valuesService.processServiceState.DONE);
                    throw err;
                })
            );
        }
    }

    /**
     * Method used for updating the password status setting.
     */
    public updatePassword(): void {
        if (this.onListPasswordStatus.value !== this.valuesService.processServiceState.INPROGRESS) {
            this.markToUpdatePassword = true;
        }
    }

    listSessions(): Observable<any> {
        if (!this.marktoUpdateSessions){
            return of(this.account);
        }

        if (this.onlistSessions.value === this.valuesService.processServiceState.INPROGRESS) {
            return this.onlistSessions.asObservable()
                .pipe(
                    skipWhile(res => res !== this.valuesService.processServiceState.DONE)
                );
        } else {
            this.onlistSessions.next(this.valuesService.processServiceState.INPROGRESS);
            return this.connectMgmtService.getListOfSerssions()
            .pipe(
                map(
                    resp => {
                        if (!this.account['sessions']) {
                            this.account['sessions'] = {};
                        }
                        this.account['sessions']['active'] = resp.sessions;
                        this.account['sessions']['closed'] = resp.closedSessions;

                        this.marktoUpdateSessions = false;
                        this.onlistSessions.next(this.valuesService.processServiceState.DONE);
                    }
                ),
                catchError( (err)=> {
                    if (!this.account['sessions']) {
                        this.account['sessions'] = {};
                    }

                    this.marktoUpdateSessions = true;
                    this.onlistSessions.next(this.valuesService.processServiceState.DONE);
                    throw err;
                })
            );
        }
    }

    /**
     * Method used for updating the sessions.
     */
    public updateSesions(): void {
        if (this.onlistSessions.value !== this.valuesService.processServiceState.INPROGRESS) {
            this.marktoUpdateSessions = true;
        }
    }

    computeSessions(successList) {
        const account = this.get();
        const activeSessions = this.account?.sessions?.active ?? [];
        const closedSessions = this.account?.sessions?.closed ?? [];

        if (successList.size !== 0) {
            for (let i = 0; i < activeSessions.length; i++) {
                if (successList.has(activeSessions[i].session_id)) {
                    activeSessions[i]['last_modified'] = Date.now();
                    closedSessions.push(activeSessions[i]);
                    activeSessions.splice(i, 1);
                    i--;
                }
            }
        }
    }

    closeSessions(arraySessions): Observable<any> {
        return this.connectMgmtService.closeSession(arraySessions)
        .pipe(
            map(
                resp => {
                    const successList = new Set();
                    const errorList = new Set();
                    if (resp) {
                        for (const sessionId in resp) {
                            if (!resp[sessionId].error) {
                                successList.add(arraySessions[sessionId]);
                            } else {
                                errorList.add(arraySessions[sessionId]);
                            }
                        }
                        this.computeSessions(successList);
                    }

                    return errorList.size === 0;
                },
                err => {
                    throw err;
                }
        ));
    }

}
